<template>
  <div class="invite-btn__outer">
    <DialogSlot
      v-if="isPicView && showInviteBtn"
      id="project-detail-invite-vendor"
      ref="inviteModal"
      :modalBtn="$t('action.invite')"
      :modalHeader="modalTitle"
      :beforeOpenModal="checkRequiredInfoBeforeInvite"
    >
      <div
        slot="content"
        class="slot-content font-bold invite-btn__content"
      >
        <div class="vendor-name">
          {{ vendorName }}
        </div>
        <div
          v-if="!isValidateTenderDlp"
          class="without-dlp"
        >
          {{ $t('project.inviteVendorWithoutDlpDate.content') }}
        </div>

        <div
          v-show="shouldShowUploadArea"
          style="padding-top: 10px;"
        >
          <label>{{ attachmentLabel }}</label>
          <AttachDocs
            :resourceId="getAttachmentId()"
            :btnName="attachBtnLabel"
            @update:attachments="val => poAttachments = val"
          />
        </div>
      </div>
      <div slot="buttons">
        <HKLButton
          v-if="!isValidateTenderDlp"
          color="darkblue"
          action="goback"
          @click="cancel"
        />
        <HKLButton
          color="orange"
          :action="inviteBtnAction"
          :disabled="shouldDisabledInviteBtn"
          @click="notifyInvitation"
        />
      </div>
    </DialogSlot>
    <DialogSlot
      id="project-detail-invite-success"
      ref="msg-modal"
      :showCloseBtn="false"
    >
      <div
        slot="content"
        class="slot-content"
      >
        <div class="info font-bold">
          {{ $t('project.invitedMsg', [projectName, vendorName]) }}
        </div>
      </div>
      <div slot="buttons">
        <button
          type="button"
          class="btn btn--orange close-reveal-modal"
          @click="navToDashboard()"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </DialogSlot>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import HKLButton from '@/controls/Button/HKLButton'
import DialogSlot from '@/controls/DialogSlot'
import AttachDocs from '@/modules/projects/AttachDocs'
import notification from '@/utils/notification'
import { projectStatus } from '@/utils/locale'
import { aclHelper } from '@/utils/aclHelper'
import { PROJECT_TYPES } from '@/modules/projects/consts'
import { sendEmail } from '@/api/postman/request'
import { ActionTypes, EmailTypes } from '@/utils/consts'
import navigation from '@/utils/navigation'

export default {
  nameL: 'ProjectInviteBtn',
  components: {
    AttachDocs,
    HKLButton,
    DialogSlot,
  },
  inject: [
    'getIsValidRetention',
    'getAttachmentId',
  ],
  data () {
    return {
      poAttachments: [],
    }
  },
  computed: {
    ...mapGetters('projects', ['currentProject', 'purchase', 'typeLabel']),
    isPicView () {
      return aclHelper.isProjectPic()
    },
    hasAttached () {
      const attachmentId = lodash.get(this.currentProject, 'poAttachmentId')
      return attachmentId && this.poAttachments.length
    },
    shouldShowUploadArea () {
      return !(this.hasAttached || this.tenderDerived)
    },
    shouldDisabledInviteBtn () {
      const hasFiles = this.poAttachments.length
      return !(hasFiles || this.tenderDerived)
    },
    showInviteBtn () {
      return lodash.get(this.currentProject, 'status') === projectStatus.DRAFT
    },
    projectName () {
      return lodash.get(this.purchase, 'description')
    },
    vendorName () {
      return lodash.get(this.purchase, 'company.name')
    },
    currentTender () {
      return lodash.get(this.purchase, ['tenders', 0])
    },
    tenderDerived () {
      return this.currentTender && Object.keys(this.currentTender).length > 0
    },
    numberType () {
      return lodash.get(this.currentProject, 'numberType')
    },
    attachmentLabel () {
      if (this.numberType === PROJECT_TYPES.CONTRACT) {
        return this.$t('project.purchase.attachment', {type: this.typeLabel.toLowerCase()})
      } else { return this.$t('project.purchase.attachment', {type: this.typeLabel}) }
    },
    attachBtnLabel () {
      return `${this.$t('action.attach')} ${this.typeLabel}`
    },
    buildingIncludedRequired () {
      return [projectStatus.DRAFT, projectStatus.ACTIVE].includes(this.currentProject.status) && this.currentProject.site.buildings && this.currentProject.site.buildings.length > 0
    },
    hasBuildingIncluded () {
      return this.currentProject.buildings && this.currentProject.buildings.length > 0
    },
    isValidateTenderDlp () {
      if (!this.currentTender) return true
      const optionalItems = lodash.get(this.currentTender, 'optionalItems')
      const tenderDlp = lodash.find(optionalItems, { name: 'Defects Liability Period' })
      const withoutDlp = !tenderDlp
      const notSelectedDlp = tenderDlp && !tenderDlp.selected
      if (withoutDlp || notSelectedDlp) return true

      const withOutProjectDlpDate = !lodash.get(this.currentProject, 'dlpDate')
      if (tenderDlp.selected && tenderDlp.value && withOutProjectDlpDate) return false

      return true
    },
    modalTitle () {
      if (this.isValidateTenderDlp) {
        return this.$t('project.inviteVendor')
      } else {
        return this.$t('project.inviteVendorWithoutDlpDate.title')
      }
    },
    inviteBtnAction () {
      return this.isValidateTenderDlp ? 'invite' : 'yesInvite'
    },
  },
  methods: {
    ...mapActions('projects', [
      'loadProject',
      'updateProject',
      'updateProjectStatus',
    ]),
    async navToDashboard () {
      await this.$refs['msg-modal'].closeModal()
      navigation.toHome()
    },

    checkRequiredInfoBeforeInvite () {
      const buildingIncludedPass = !this.buildingIncludedRequired || this.hasBuildingIncluded
      const retentionPass = this.getIsValidRetention()
      const pass = buildingIncludedPass && retentionPass
      if (!pass) {
        notification.fireNotification(false, `${
          buildingIncludedPass ? '' : this.$t('project.buildingIncluded.missing')
        } ${
          retentionPass ? '' : this.$t('project.retention.missing')
        }`.trim())
        return false
      }
      return true
    },
    cancel () {
      this.$refs.inviteModal.closeModal()
    },
    async notifyInvitation () {
      try {
        await this.updateProject({
          projectId: this.currentProject.id,
          request: { poAttachmentId: this.getAttachmentId() },
        })
        const results = await sendEmail(EmailTypes.projects.invitation)
        if (!results || !results[0].isSuccess) {
          return
        }
        await this.updateProjectStatus({
          id: this.currentProject.id,
          status: projectStatus.ACTIVE,
          action: ActionTypes.projects.invite,
        })
        await this.loadProject(this.currentProject.id)
        this.$refs['msg-modal'].openModal()
      } catch (err) {
        notification.fireNotification(false, err.message)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.invite-btn {
  &__content {
    .without-dlp {
      margin-top: 20px;
    }
  }
}
</style>
